import Component from '@glimmer/component';
import type { CartesianScaleTypeRegistry, ChartConfiguration, ScaleOptionsByType } from 'chart.js';
import type { DataSet } from '../../modifiers/chart';

export interface LineChartSignature {
  Args: {
    ariaLabel: string;
    annotationLines?: {
      label: string;
      value: number;
    }[];
    datasets: DataSet[];
    loading?: boolean;
    noDataMessage?: string;
    options?: {
      stepSize?: number;
      min?: number;
      max?: number;
      maintainAspectRatio?: boolean;
      fillAreaUnderCurve?: boolean;
      showTimeStampsOnAxis?: boolean;
      yAxisTickUnit?: string;
      xAxis?: Partial<ScaleOptionsByType<keyof CartesianScaleTypeRegistry>>;
      yAxis?: Partial<ScaleOptionsByType<keyof CartesianScaleTypeRegistry>>;
    };
    unit?: string;
  };
  Element: HTMLCanvasElement;
}

const colors: { className: string; hex: string }[] = [
  { className: 'bg-primary-400', hex: '#165142' },
  { className: 'bg-primary-700', hex: '#499682' },
  { className: 'gray-transparent-500', hex: '#0A0A0A85' },
  { className: 'gray-opaque-100', hex: '#E9E9E7' },
];

const ERROR_COLOR = '#CD3026';

export default class LineChartComponent extends Component<LineChartSignature> {
  colorOption = (index: number) => {
    return colors[index]?.className;
  };

  get showTimeStampsOnAxis() {
    return this.args.options?.showTimeStampsOnAxis ?? false;
  }

  get datasetsWithOptions() {
    const fillAreaUnderCurve =
      this.args.options?.fillAreaUnderCurve || this.args.options?.fillAreaUnderCurve === undefined;

    return this.args.datasets.map((dataset, index) => ({
      ...dataset,
      color: colors[index]?.hex,
      pointRadius: 0, // Hide points by default
      pointHoverRadius: 4,
      pointHoverBackgroundColor: dataset.error ? ERROR_COLOR : colors[index]?.hex,
      pointHoverBorderColor: dataset.error ? ERROR_COLOR : colors[index]?.hex,
      borderWidth: fillAreaUnderCurve ? 1 : 2,
      tension: 0.3,
      ...(fillAreaUnderCurve && {
        fill: {
          target: 'origin',
          above: `${colors[index]?.hex}4D`,
        },
      }),
    }));
  }

  get dataPresent() {
    return (
      this.datasetsWithOptions.length &&
      this.datasetsWithOptions.some((dataset) => dataset.data.length)
    );
  }

  get options(): Partial<ChartConfiguration<'line'>> {
    return {
      type: 'line',
      options: {
        interaction: {
          intersect: false,
          mode: 'index',
        },
        maintainAspectRatio: this.args.options?.maintainAspectRatio ?? true,
        plugins: {
          legend: {
            display: false,
          },
          annotation: {
            annotations: this.args.annotationLines?.reduce((acc, curr, index) => {
              return {
                [`line-${index}`]: {
                  type: 'line',
                  label: {
                    display: true,
                    content: curr.label,
                    backgroundColor: 'transparent',
                    color: '#B0B0AC',
                    position: 'start',
                    yAdjust: index === 0 ? -10 : 10,
                  },
                  yMin: curr.value,
                  yMax: curr.value,
                  borderColor: '#B0B0AC',
                  borderWidth: 1,
                },
                ...acc,
              };
            }, {}),
          },
          tooltip: {
            callbacks: {
              labelColor: (context) => {
                return {
                  borderColor: colors[context.datasetIndex]?.hex as string,
                  backgroundColor: colors[context.datasetIndex]?.hex as string,
                };
              },
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                const unit = this.args.unit || '';

                return `${label}: ${value}${unit ? ' ' + unit : ''}`;
              },
            },
          },
        },
        hover: {
          intersect: false,
        },
        borderColor: (context) => {
          // @ts-expect-error not sure there is a good way to type this
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return context.dataset.error ? ERROR_COLOR : context.dataset.color;
        },
        scales: {
          y: {
            grid: {
              drawTicks: false,
              color: colors.find(({ className }) => 'gray-opaque-100' === className)?.hex,
            },
            ticks: {
              padding: 10,
              color: colors.find(({ className }) => 'gray-transparent-500' === className)?.hex,
              stepSize: this.args.options?.stepSize || 20,
              callback: (tickValue) => {
                return this.args.options?.yAxisTickUnit
                  ? `${tickValue} ${this.args.options?.yAxisTickUnit}`
                  : tickValue;
              },
            },
            min: this.args.options?.min || 0,
            max: this.args.options?.max || 80,
            border: {
              display: false,
            },
            ...((this.args.options?.yAxis
              ? {
                  ...this.args.options.yAxis,
                }
              : {}) as Record<string, unknown>),
          },
          x: {
            display: this.showTimeStampsOnAxis,
            grid: {
              display: this.showTimeStampsOnAxis,
            },
            border: {
              display: true,
            },
            ...(this.args.options?.xAxis
              ? {
                  ...this.args.options.xAxis,
                }
              : {
                  ...(this.showTimeStampsOnAxis && {
                    type: 'time',
                    time: {
                      displayFormats: {
                        hour: 'HH:mm',
                      },
                    },
                  }),
                }),
          },
        },
      },
    };
  }
}
